import { inject } from '@angular/core';
import { CanActivateChildFn, CanActivateFn, Router } from '@angular/router';
import { of, switchMap, take } from 'rxjs';
import { NavigationCacheService } from 'app/core/navigation/navigation-cache.service';

/**
 * Global Route Permission Guard
 *
 * Validates that user can only access routes that exist in their navigation menu.
 * Uses NavigationCacheService which subscribes to NavigationService (single source of truth).
 *
 * Features:
 * - Ensures navigation is loaded from API before validating (calls ensureNavigationLoaded())
 * - O(1) route lookup using Set<string>
 * - Blocks route activation BEFORE rendering any component
 * - Redirects to first available route if access denied
 * - Works with canActivate and canActivateChild for automatic application to child routes
 *
 * Flow:
 * 1. Guard executes
 * 2. Calls ensureNavigationLoaded() which:
 *    - If data exists → returns immediately
 *    - If no data → calls NavigationService.get() to fetch from API
 * 3. Once loaded, validates route permission
 * 4. If allowed → return true (allow navigation)
 * 5. If not allowed → return UrlTree (redirect without rendering)
 *
 * Usage:
 * Add to parent routes with canActivateChild to automatically protect all child routes:
 * {
 *   path: 'master',
 *   canActivate: [AuthGuard, RoutePermissionGuard],
 *   canActivateChild: [AuthGuard, RoutePermissionGuard],
 *   children: [ ... ] // All children automatically protected
 * }
 */
export const RoutePermissionGuard: CanActivateFn | CanActivateChildFn = (route, state) => {
    const navigationCache = inject(NavigationCacheService);
    const router = inject(Router);

    // Ensure navigation is loaded (calls API if needed, or returns immediately if cached)
    return navigationCache.ensureNavigationLoaded().pipe(
        switchMap((loaded) => {
            // If failed to load navigation, redirect to home
            if (!loaded) {
                return of(router.parseUrl('/home'));
            }

            const targetUrl = state.url;

            // Check if route is allowed (O(1) lookup with Set.has())
            const isAllowed = navigationCache.isRouteAllowed(targetUrl);

            if (isAllowed) {
                return of(true); // Allow navigation
            }

            // Access denied - redirect to first available route
            const redirectUrl = navigationCache.getFirstRoute();

            // Prevent infinite redirect loop
            if (targetUrl === redirectUrl) {
                return of(true);
            }

            // Return UrlTree to redirect WITHOUT rendering the unauthorized component
            // This is key: Angular won't activate the route, so nothing renders
            return of(router.parseUrl(redirectUrl));
        }),
        take(1) // Complete immediately after check
    );
};
